import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ImageSelector } from '../components/ImageSelector';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    ArrowLeft, 
    Plus, 
    Trash2, 
    Save, 
    Loader2,
    ChefHat,
    Sparkles,
    DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

export const RecipeFormPage = () => {
    const { id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const isEditing = !!id;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [estimatingPrice, setEstimatingPrice] = useState({});
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'dinner',
        categories: ['dinner'],
        difficulty: 'facile',
        prep_time: 15,
        cook_time: 30,
        servings: 4,
        image_url: '',
        tags: [],
        ingredients: [{ name: '', quantity: '', unit: 'g', estimated_cost: '' }],
        steps: ['']
    });

    const categories = [
        { value: 'breakfast', label: 'Déjeuner' },
        { value: 'lunch', label: 'Dîner' },
        { value: 'dinner', label: 'Souper' },
        { value: 'soups', label: 'Soupes' },
        { value: 'appetizers', label: 'Entrées' },
        { value: 'salads', label: 'Salades' },
        { value: 'sides', label: 'Accompagnements' },
        { value: 'dessert', label: 'Dessert' },
        { value: 'snack', label: 'Collation' },
        { value: 'basics', label: 'Les bases' }
    ];

    const difficulties = [
        { value: 'facile', label: 'Facile' },
        { value: 'moyen', label: 'Moyen' },
        { value: 'difficile', label: 'Difficile' }
    ];

    const units = ['g', 'kg', 'ml', 'L', 'tasse', 'c. à soupe', 'c. à thé', 'pièces', 'tranche(s)', 'pincée'];

    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login');
            return;
        }
        
        if (user && isEditing) {
            loadRecipe();
        }
    }, [user, authLoading, id, navigate, isEditing]);

    const loadRecipe = async () => {
        setLoading(true);
        try {
            const response = await api.getRecipe(id);
            const recipe = response.data;
            
            // Check ownership
            if (recipe.author_id !== user.id) {
                toast.error('Vous ne pouvez pas modifier cette recette');
                navigate('/my-recipes');
                return;
            }
            
            setFormData({
                ...recipe,
                tags: recipe.tags || []
            });
        } catch (error) {
            console.error('Error loading recipe:', error);
            toast.error('Erreur lors du chargement de la recette');
            navigate('/my-recipes');
        } finally {
            setLoading(false);
        }
    };

    const calculateCostPerServing = () => {
        const totalCost = formData.ingredients.reduce((sum, ing) => {
            return sum + (parseFloat(ing.estimated_cost) || 0);
        }, 0);
        return formData.servings > 0 ? (totalCost / formData.servings).toFixed(2) : '0.00';
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleIngredientChange = async (index, field, value) => {
        setFormData(prev => {
            const ingredients = [...prev.ingredients];
            ingredients[index] = { ...ingredients[index], [field]: value };
            return { ...prev, ingredients };
        });
        
        // Auto-estimate price when ingredient name changes and loses focus or after typing
        if (field === 'name' && value.length >= 3) {
            // Debounce - wait a bit before searching
            setTimeout(() => autoEstimatePrice(index, value), 500);
        }
    };

    const autoEstimatePrice = async (index, ingredientName) => {
        if (!ingredientName || ingredientName.length < 3) return;
        
        // Check if we already have a price for this ingredient
        const currentCost = formData.ingredients[index]?.estimated_cost;
        if (currentCost && parseFloat(currentCost) > 0) return;
        
        setEstimatingPrice(prev => ({ ...prev, [index]: true }));
        
        try {
            // First try database lookup
            const response = await api.getIngredientPrice(ingredientName);
            
            if (response.data.found) {
                setFormData(prev => {
                    const ingredients = [...prev.ingredients];
                    if (ingredients[index]) {
                        ingredients[index] = { 
                            ...ingredients[index], 
                            estimated_cost: response.data.price.toFixed(2)
                        };
                    }
                    return { ...prev, ingredients };
                });
                
                if (response.data.source === 'database') {
                    toast.success(`Prix trouvé: ${response.data.price.toFixed(2)}$ / ${response.data.unit}`, {
                        duration: 2000
                    });
                }
            }
        } catch (error) {
            console.log('Price estimation not available');
        } finally {
            setEstimatingPrice(prev => ({ ...prev, [index]: false }));
        }
    };

    const estimatePriceWithAI = async (index) => {
        const ingredient = formData.ingredients[index];
        if (!ingredient?.name) {
            toast.error('Entrez le nom de l\'ingrédient d\'abord');
            return;
        }
        
        setEstimatingPrice(prev => ({ ...prev, [index]: true }));
        
        try {
            const response = await api.estimateIngredientPrice(
                ingredient.name, 
                `${ingredient.quantity} ${ingredient.unit}`
            );
            
            if (response.data.found) {
                setFormData(prev => {
                    const ingredients = [...prev.ingredients];
                    ingredients[index] = { 
                        ...ingredients[index], 
                        estimated_cost: response.data.price.toFixed(2)
                    };
                    return { ...prev, ingredients };
                });
                
                const sourceLabel = response.data.source === 'ai' ? '(IA)' : '';
                toast.success(`Prix estimé ${sourceLabel}: ${response.data.price.toFixed(2)}$`, {
                    duration: 3000
                });
            } else {
                toast.error('Impossible d\'estimer le prix');
            }
        } catch (error) {
            toast.error('Erreur lors de l\'estimation');
        } finally {
            setEstimatingPrice(prev => ({ ...prev, [index]: false }));
        }
    };

    const addIngredient = () => {
        setFormData(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, { name: '', quantity: '', unit: 'g', estimated_cost: '' }]
        }));
    };

    const removeIngredient = (index) => {
        setFormData(prev => ({
            ...prev,
            ingredients: prev.ingredients.filter((_, i) => i !== index)
        }));
    };

    const handleStepChange = (index, value) => {
        setFormData(prev => {
            const steps = [...prev.steps];
            steps[index] = value;
            return { ...prev, steps };
        });
    };

    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, '']
        }));
    };

    const removeStep = (index) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index)
        }));
    };

    const handleTagsChange = (value) => {
        const tags = value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        setFormData(prev => ({ ...prev, tags }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.title || !formData.description) {
            toast.error('Veuillez remplir le titre et la description');
            return;
        }
        
        if (formData.ingredients.length === 0 || !formData.ingredients[0].name) {
            toast.error('Ajoutez au moins un ingrédient');
            return;
        }
        
        if (formData.steps.length === 0 || !formData.steps[0]) {
            toast.error('Ajoutez au moins une étape');
            return;
        }
        
        setSaving(true);
        try {
            const data = {
                ...formData,
                cost_per_serving: parseFloat(calculateCostPerServing()),
                prep_time: parseInt(formData.prep_time),
                cook_time: parseInt(formData.cook_time),
                servings: parseInt(formData.servings),
                ingredients: formData.ingredients.filter(ing => ing.name),
                steps: formData.steps.filter(step => step.trim())
            };
            
            if (isEditing) {
                await api.updateRecipe(id, data);
                toast.success('Recette mise à jour!');
            } else {
                await api.createRecipe(data);
                toast.success('Recette soumise avec succès! Elle sera visible après modération.');
            }
            
            navigate('/my-recipes');
        } catch (error) {
            console.error('Error saving recipe:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 md:py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour
                    </Button>
                    
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                            <ChefHat className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                                {isEditing ? 'Modifier la recette' : 'Proposer une recette'}
                            </h1>
                            <p className="text-muted-foreground">
                                {isEditing ? 'Mettez à jour votre recette' : 'Partagez votre recette économique avec la communauté'}
                            </p>
                        </div>
                    </div>
                    
                    {/* Moderation Notice */}
                    {!isEditing && (
                        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xl">📝</span>
                            </div>
                            <div>
                                <p className="font-semibold text-amber-800">Soumission à modération</p>
                                <p className="text-sm text-amber-700">
                                    Votre recette sera examinée par notre équipe avant d&apos;être publiée. 
                                    Vous recevrez une notification une fois approuvée. Merci de contribuer!
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Info */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading">Informations générales</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre de la recette *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    placeholder="Ex: Pâtes à la carbonara économique"
                                    className="rounded-xl"
                                    data-testid="recipe-title-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="description">Description *</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Décrivez votre recette en quelques mots..."
                                    className="rounded-xl min-h-[100px]"
                                    data-testid="recipe-description-input"
                                />
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Catégories (plusieurs possibles)</Label>
                                    <div className="flex flex-wrap gap-2 p-3 border rounded-xl bg-background min-h-[42px]">
                                        {(formData.categories || [formData.category]).map(cat => {
                                            const catInfo = categories.find(c => c.value === cat);
                                            return catInfo ? (
                                                <Badge 
                                                    key={cat}
                                                    variant="secondary"
                                                    className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                                    onClick={() => {
                                                        const newCats = (formData.categories || [formData.category]).filter(c => c !== cat);
                                                        if (newCats.length > 0) {
                                                            setFormData(prev => ({ ...prev, categories: newCats, category: newCats[0] }));
                                                        }
                                                    }}
                                                >
                                                    {catInfo.label} ×
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                    <select
                                        className="w-full border rounded-xl p-2 text-sm"
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                const currentCats = formData.categories || [formData.category];
                                                if (!currentCats.includes(e.target.value)) {
                                                    setFormData(prev => ({ 
                                                        ...prev, 
                                                        categories: [...currentCats, e.target.value],
                                                        category: currentCats[0] || e.target.value
                                                    }));
                                                }
                                            }
                                        }}
                                        data-testid="recipe-category-select"
                                    >
                                        <option value="">+ Ajouter une catégorie...</option>
                                        {categories.filter(cat => !(formData.categories || [formData.category]).includes(cat.value)).map(cat => (
                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Difficulté</Label>
                                    <Select value={formData.difficulty} onValueChange={(v) => handleInputChange('difficulty', v)}>
                                        <SelectTrigger className="rounded-xl" data-testid="recipe-difficulty-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {difficulties.map(diff => (
                                                <SelectItem key={diff.value} value={diff.value}>{diff.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="grid sm:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="prep_time">Préparation (min)</Label>
                                    <Input
                                        id="prep_time"
                                        type="number"
                                        min="0"
                                        value={formData.prep_time}
                                        onChange={(e) => handleInputChange('prep_time', e.target.value)}
                                        className="rounded-xl"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="cook_time">Cuisson (min)</Label>
                                    <Input
                                        id="cook_time"
                                        type="number"
                                        min="0"
                                        value={formData.cook_time}
                                        onChange={(e) => handleInputChange('cook_time', e.target.value)}
                                        className="rounded-xl"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="servings">Portions</Label>
                                    <Input
                                        id="servings"
                                        type="number"
                                        min="1"
                                        value={formData.servings}
                                        onChange={(e) => handleInputChange('servings', e.target.value)}
                                        className="rounded-xl"
                                    />
                                </div>
                            </div>
                            
                            <ImageSelector
                                value={formData.image_url}
                                onChange={(url) => handleInputChange('image_url', url)}
                                label="Photo de la recette (optionnel)"
                            />
                            
                            <div className="space-y-2">
                                <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
                                <Input
                                    id="tags"
                                    value={formData.tags.join(', ')}
                                    onChange={(e) => handleTagsChange(e.target.value)}
                                    placeholder="économique, rapide, végétarien..."
                                    className="rounded-xl"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ingredients */}
                    <Card className="rounded-2xl">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="font-heading">Ingrédients</CardTitle>
                            <div className="text-right">
                                <p className="text-sm text-muted-foreground">Coût estimé/portion</p>
                                <p className="text-2xl font-bold text-secondary">{calculateCostPerServing()}$</p>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {formData.ingredients.map((ing, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="flex-1 grid grid-cols-4 gap-2">
                                        <Input
                                            placeholder="Nom (ex: poulet, oignon...)"
                                            value={ing.name}
                                            onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                                            className="rounded-xl col-span-2"
                                            data-testid={`ingredient-name-${index}`}
                                        />
                                        <Input
                                            placeholder="Qté"
                                            value={ing.quantity}
                                            onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                                            className="rounded-xl"
                                        />
                                        <Select value={ing.unit} onValueChange={(v) => handleIngredientChange(index, 'unit', v)}>
                                            <SelectTrigger className="rounded-xl">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {units.map(unit => (
                                                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="relative">
                                            <Input
                                                placeholder="Coût $"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={ing.estimated_cost}
                                                onChange={(e) => handleIngredientChange(index, 'estimated_cost', e.target.value)}
                                                className={`rounded-xl w-24 pr-8 ${estimatingPrice[index] ? 'animate-pulse bg-green-50' : ''}`}
                                            />
                                            {estimatingPrice[index] && (
                                                <Loader2 className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-green-500" />
                                            )}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => estimatePriceWithAI(index)}
                                            disabled={estimatingPrice[index] || !ing.name}
                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                            title="Estimer le prix (Québec)"
                                        >
                                            <DollarSign className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeIngredient(index)}
                                        disabled={formData.ingredients.length === 1}
                                        className="text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                            
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addIngredient}
                                className="w-full rounded-xl"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter un ingrédient
                            </Button>
                            
                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-4">
                                <p className="text-sm text-green-700 flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    <span>
                                        <strong>Prix automatiques :</strong> Les prix sont estimés selon les épiceries économiques du Québec (Maxi, Super C). 
                                        Cliquez sur <DollarSign className="w-3 h-3 inline" /> pour estimer un ingrédient.
                                    </span>
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Steps */}
                    <Card className="rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading">Étapes de préparation</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {formData.steps.map((step, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-2">
                                        <span className="font-bold text-primary text-sm">{index + 1}</span>
                                    </div>
                                    <Textarea
                                        value={step}
                                        onChange={(e) => handleStepChange(index, e.target.value)}
                                        placeholder={`Étape ${index + 1}...`}
                                        className="rounded-xl flex-1 min-h-[80px]"
                                        data-testid={`step-${index}`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeStep(index)}
                                        disabled={formData.steps.length === 1}
                                        className="text-destructive mt-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                            
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addStep}
                                className="w-full rounded-xl"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter une étape
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Submit */}
                    <div className="flex gap-4 justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="rounded-xl"
                        >
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-8"
                            data-testid="save-recipe-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    {isEditing ? 'Mettre à jour' : 'Publier la recette'}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecipeFormPage;
